import React, { useContext } from 'react';
import { GridItem, Tag, TagRoot, VStack } from '@chakra-ui/react';
import { emptyCharacter, SPACING } from '../../generic/config';
import { Context } from '../../App';
import Card from '../../base-components/Card';
import { Model } from '../../base-components/hooks/useModel';
import { AttachedFile } from '@/model/attached-file.schema';
import uuid from '@/generic/uuid-utils';
import Fileupload from '@/base-components/Fileupload';
import Text from '@/base-components/Text';
import Icon, { Icons } from '@/base-components/Icons';
import { UUIDTypes } from 'uuid';
import useDialog from '@/base-components/hooks/useDialog';
import { isDesktop } from 'react-device-detect';
import useToast from '@/base-components/hooks/useToast';

type WithAttachedFiles = { id: number, attachedFiles: AttachedFile[] };

const AttachedFilesGridItem = <T extends WithAttachedFiles>({ model, previewAttachment, colSpan }: { model: Model<T>, previewAttachment: (id: number, uuid: UUIDTypes) => Promise<string>, colSpan?: number }) => {
    const { translations } = React.useContext(Context);
    const dialog = useDialog();
    const toast = useToast();

    const onPreview = (uuid: UUIDTypes, targetFileName: string) => {
        if (model.object.attachedFiles.find(f => f.uuid === uuid)?.file) {
            toast(translations.attachedFiles.newPreviewDisabled.title, translations.attachedFiles.newPreviewDisabled.description, "info");
            return;
        }

        previewAttachment(model.object.id, uuid).then((previewUrl) => {
            dialog.openDialog({
                title: translations.attachedFiles.filePreview,
                body: <AttachmentPreview previewUrl={previewUrl} />,
                buttons: [{
                    title: translations.attachedFiles.downloadFile, onClick: () => {
                        const link = document.createElement('a');
                        link.href = previewUrl;
                        link.download = targetFileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                }],
                size: "xl"
            });
        });
    }
    return (
        <GridItem colSpan={colSpan}>
            <Card {...translations.attachedFiles} rightAddon={
                <Fileupload disabled={model.isNew} title={translations.attachedFiles.attach} onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                        const name = file.name.substring(0, file.name.lastIndexOf('.'));
                        const extension = file.name.split('.').pop();
                        const newFile: AttachedFile = { uuid: uuid(), name, extension: extension ?? "", modifiedAt: new Date(), size: file.size, file };
                        model.setObject({
                            ...model.object,
                            attachedFiles: [...model.object.attachedFiles, newFile]
                        });

                        // clear file input
                        e.target.value = '';
                    }
                }} />
            }>
                <VStack alignItems="start" gap={SPACING}>
                    {model.object.attachedFiles.sort((a, b) => new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()).map((file) => (
                        <TagRoot key={file.uuid} w="100%" size="sm" py={SPACING / 4}>
                            <Tag.Label w="100%">
                                <Text maxW="40vw" variant='small' fontWeight="medium" cursor="pointer" onClick={() => onPreview(file.uuid, file.name + '.' + file.extension)}>{file.name}.{file.extension}</Text>
                            </Tag.Label>
                            {isDesktop && (
                                <Text w="32ch" variant='extra-small' fontWeight="medium" whiteSpace="nowrap">
                                    {new Date(file.modifiedAt).toLocaleDateString()}
                                    {emptyCharacter.repeat(3)}
                                    {new Date(file.modifiedAt).toLocaleTimeString()}
                                </Text>
                            )}
                            <Text textAlign="right" w="12ch" variant='extra-small' fontWeight="medium" whiteSpace="nowrap">
                                {(file.size / 1024).toFixed(1)} KB
                            </Text>
                            <Icon size="xs" icon={Icons.preview} onClick={() => onPreview(file.uuid, file.name + '.' + file.extension)} />
                            <Tag.EndElement>
                                <Tag.CloseTrigger onClick={() => model.setObject({
                                    ...model.object,
                                    attachedFiles: model.object.attachedFiles.filter(f => f.uuid !== file.uuid)
                                })} />
                            </Tag.EndElement>
                        </TagRoot>
                    ))}
                </VStack>
            </Card>
        </GridItem >
    );
}

export default AttachedFilesGridItem;

const AttachmentPreview = ({ previewUrl }: { previewUrl: string }) => {
    const { translations } = useContext(Context);
    const [error, setError] = React.useState(false);

    return (
        <div style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}>
            {error ? (
                <div>
                    <p>{translations.attachedFiles.cannotPreview}</p>
                </div>
            ) : (
                <img
                    src={previewUrl}
                    alt=""
                    onError={() => setError(true)}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                        margin: '0 auto'
                    }}
                />
            )}
        </div>

    );
};